import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ColorSchemeType, Colorset } from '../../models/colorset';
import { CrudService } from '../crud.service';
import chroma from 'chroma-js';

@Injectable({
  providedIn: 'root',
})
export class ColorsetService extends CrudService<Colorset> {
  protected basePath = 'colorset'; // Specific for ColorsetService
  public instanceType = 'colorset'; // Used in base service for constructing URLs

  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Generates a color scheme based on a starting color and a scheme type.
   * @param baseColor The base hex color code to start from.
   * @param count The number of colors to generate in the scheme.
   * @param schemeType The type of color scheme to generate.
   * @returns An array of hex color codes.
   */
  generateColorScheme(
    baseColor: string,
    count: number,
    schemeType: ColorSchemeType
  ): string[] {
    let colors: string[] = [];
    switch (schemeType) {
      case 'complementary':
        colors = chroma
          .scale([baseColor, chroma(baseColor).set('hsl.h', '+180')])
          .mode('lch')
          .colors(count);
        break;
      case 'analogous':
        // eslint-disable-next-line no-case-declarations
        const start = chroma(baseColor).get('hsl.h') - 30;
        colors = chroma
          .scale([
            chroma.hsl(start, 1, 0.5),
            baseColor,
            chroma.hsl(start + 60, 1, 0.5),
          ])
          .mode('lch')
          .colors(count);
        break;
      case 'triadic':
        colors = [
          chroma(baseColor).hex(),
          chroma(baseColor).set('hsl.h', '+120').hex(),
          chroma(baseColor).set('hsl.h', '+240').hex(),
        ];
        if (count > 3) {
          colors = chroma.scale(colors).mode('lch').colors(count);
        }
        break;
      case 'tetradic':
        colors = [
          chroma(baseColor).hex(),
          chroma(baseColor).set('hsl.h', '+90').hex(),
          chroma(baseColor).set('hsl.h', '+180').hex(),
          chroma(baseColor).set('hsl.h', '+270').hex(),
        ];
        if (count > 4) {
          colors = chroma.scale(colors).mode('lch').colors(count);
        }
        break;
    }
    return colors;
  }
}
